import type { NavigateFunction } from 'react-router-dom';
import { TOKEN_KEY } from './client.js';
import { LAST_LOGIN_ENTRY_KEY, type LoginEntryPath } from './login-entry.js';

type WebAppKind = 'crest' | 'crm' | 'erp';

const LOCAL_WEB_PORTS: Record<WebAppKind, string> = {
  crest: '5174',
  crm: '5175',
  erp: '5176',
};

const APP_KIND: Record<string, WebAppKind> = {
  admin: 'crest',
  operations: 'crest',
  reporting: 'crest',
  // 人力中心（hr）无独立旧壳，由统一门户 host 承载（页面经 hr_remote 或域源码包装配）
  hr: 'crest',
  crm: 'crm',
  finance: 'erp',
};

const LOGIN_ENTRY: Record<WebAppKind, '/crest' | '/crm' | '/erp'> = {
  crest: '/crest',
  crm: '/crm',
  erp: '/erp',
};

export const CROSS_APP_TOKEN_HASH_KEY = 'crest_access_token';
export const CROSS_APP_LOGIN_ENTRY_HASH_KEY = 'crest_login_entry';
export const CROSS_APP_PORTAL_SESSION_HASH_KEY = 'crest_portal_session';
export const PORTAL_APP_SWITCHING_KEY = 'crest_portal_app_switching';
export const PORTAL_APP_SWITCHING_EVENT = 'crest:portal-app-switching-change';

export function navigateToAppPath(targetPath: string, navigate: NavigateFunction): void {
  const appName = extractAppName(targetPath);
  const targetKind = appKindFor(appName);

  // 统一门户（M-portal）：crest-web 承载全部 appName，任何应用切换都是站内路由。
  // crm-web / erp-web 旧壳保留原跨壳跳转逻辑（回退部署形态仍可独立工作）。
  if (currentWebAppKind() === 'crest' || targetKind === currentWebAppKind()) {
    navigate(targetPath);
    return;
  }

  window.location.assign(buildCrossAppUrl(targetKind, targetPath));
}

export function navigateToAppName(appName: string, navigate: NavigateFunction): void {
  navigateToAppPath(defaultAppRoute(appName), navigate);
}

/** 应用级入口必须稳定，避免不同导航入口落到空白的应用根路由。 */
export function defaultAppRoute(appName: string): string {
  const normalized = appName.toLowerCase();
  if (normalized === 'crm') return '/app/crm';
  if (normalized === 'operations' || normalized === 'reporting') return '/app/operations';
  if (normalized === 'agent') return '/app/agent';
  return `/app/${normalized}`;
}

export function buildCrossAppUrl(targetKind: WebAppKind, targetPath: string): string {
  const baseUrl = resolveWebBaseUrl(targetKind);
  const url = new URL(targetPath, `${baseUrl}/`);
  const token = localStorage.getItem(TOKEN_KEY);
  const isPortalSession = shouldUsePortalAppSwitching();

  if (token) {
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    hash.set(CROSS_APP_TOKEN_HASH_KEY, token);
    hash.set(CROSS_APP_LOGIN_ENTRY_HASH_KEY, isPortalSession ? '/crest' : LOGIN_ENTRY[targetKind]);
    if (isPortalSession) {
      hash.set(CROSS_APP_PORTAL_SESSION_HASH_KEY, '1');
    }
    url.hash = hash.toString();
  }

  return url.toString();
}

export function buildLoginEntryUrl(entry: LoginEntryPath): string {
  if (entry === '/platform' || entry === '/agent' || entry === '/operate') {
    return buildAppRelativeUrl('crest', entry);
  }
  if (entry === '/crest') {
    return buildAppRelativeUrl('crest', '/crest');
  }
  if (entry === '/login') {
    return buildPlatformLoginUrl();
  }
  if (entry === '/crm') {
    return buildAppRelativeUrl('crm', '/crm');
  }
  return buildAppRelativeUrl('erp', '/erp');
}

export function buildPlatformLoginUrl(): string {
  return buildAppRelativeUrl('crest', '/login');
}

export function redirectToPlatformLogin(): void {
  if (typeof window === 'undefined') return;
  window.location.assign(buildPlatformLoginUrl());
}

export function consumeCrossAppTokenFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const hashText = window.location.hash.replace(/^#/, '');
  if (!hashText) return false;

  const hash = new URLSearchParams(hashText);
  const token = hash.get(CROSS_APP_TOKEN_HASH_KEY);
  if (!token) return false;

  localStorage.setItem(TOKEN_KEY, token);
  const loginEntry = hash.get(CROSS_APP_LOGIN_ENTRY_HASH_KEY);
  if (
    loginEntry === '/platform' ||
    loginEntry === '/crm' ||
    loginEntry === '/agent' ||
    loginEntry === '/operate' ||
    loginEntry === '/crest' ||
    loginEntry === '/erp'
  ) {
    localStorage.setItem(LAST_LOGIN_ENTRY_KEY, loginEntry);
  }
  if (loginEntry === '/crest' || hash.get(CROSS_APP_PORTAL_SESSION_HASH_KEY) === '1') {
    setPortalAppSwitchingSession(true);
  }

  hash.delete(CROSS_APP_TOKEN_HASH_KEY);
  hash.delete(CROSS_APP_LOGIN_ENTRY_HASH_KEY);
  hash.delete(CROSS_APP_PORTAL_SESSION_HASH_KEY);
  const cleanHash = hash.toString();
  const cleanUrl = `${window.location.pathname}${window.location.search}${
    cleanHash ? `#${cleanHash}` : ''
  }`;
  window.history.replaceState(window.history.state, '', cleanUrl);
  return true;
}

export function isPortalAppSwitchingSession(): boolean {
  return (
    typeof localStorage !== 'undefined' && localStorage.getItem(PORTAL_APP_SWITCHING_KEY) === '1'
  );
}

export function setPortalAppSwitchingSession(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  if (enabled) {
    localStorage.setItem(PORTAL_APP_SWITCHING_KEY, '1');
  } else {
    localStorage.removeItem(PORTAL_APP_SWITCHING_KEY);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PORTAL_APP_SWITCHING_EVENT));
  }
}

function extractAppName(path: string): string {
  const match = /^\/app\/([^/?#]+)/.exec(path);
  return match?.[1] ?? 'admin';
}

function appKindFor(appName: string): WebAppKind {
  return APP_KIND[appName] ?? 'crest';
}

function shouldUsePortalAppSwitching(): boolean {
  return (
    currentWebAppKind() === 'crest' ||
    isPortalAppSwitchingSession() ||
    localStorage.getItem(LAST_LOGIN_ENTRY_KEY) === '/crest'
  );
}

function currentWebAppKind(): WebAppKind {
  if (typeof __CREST_WEB_PROJECT__ !== 'undefined') {
    if (__CREST_WEB_PROJECT__ === 'crm-web') return 'crm';
    if (__CREST_WEB_PROJECT__ === 'erp-web') return 'erp';
    if (__CREST_WEB_PROJECT__ === 'crest-web' || __CREST_WEB_PROJECT__ === 'crest-platform') {
      return 'crest';
    }
  }

  if (typeof window === 'undefined') return 'crest';
  const port = window.location.port;
  if (port === LOCAL_WEB_PORTS.crm) return 'crm';
  if (port === LOCAL_WEB_PORTS.erp) return 'erp';
  if (port === LOCAL_WEB_PORTS.crest) return 'crest';

  return appKindFor(extractAppName(window.location.pathname));
}

function resolveWebBaseUrl(kind: WebAppKind): string {
  const envUrl =
    kind === 'crm'
      ? import.meta.env.VITE_CRM_WEB_BASE_URL
      : kind === 'erp'
        ? import.meta.env.VITE_ERP_WEB_BASE_URL
        : import.meta.env.VITE_CREST_WEB_BASE_URL;
  if (envUrl) return trimTrailingSlash(envUrl);

  if (typeof window === 'undefined') {
    return `http://localhost:${LOCAL_WEB_PORTS[kind]}`;
  }

  const { protocol, hostname, origin } = window.location;
  if (isLocalHost(hostname)) {
    return `${protocol}//${hostname}:${LOCAL_WEB_PORTS[kind]}`;
  }
  if (isSeparatedWebPort(window.location.port)) {
    return `${protocol}//${hostname}:${LOCAL_WEB_PORTS[kind]}`;
  }
  return origin;
}

function buildAppRelativeUrl(kind: WebAppKind, path: string): string {
  if (currentWebAppKind() === kind) return path;
  const targetBaseUrl = resolveWebBaseUrl(kind);
  if (typeof window === 'undefined') return path;
  return new URL(path, `${targetBaseUrl}/`).toString();
}

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function isSeparatedWebPort(port: string): boolean {
  return Object.values(LOCAL_WEB_PORTS).includes(port);
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
